import { z } from 'zod';

import { correlationIdSchema, isoDateTimeSchema, uuidSchema } from '../common/primitives';

/**
 * Das Prüfprotokoll — wer hat was getan.
 *
 * ## Eine Tabelle je Modul, keine Sammeltabelle
 *
 * Dieses Schema beschreibt die **Form** eines Eintrags, nicht seinen
 * Speicherort. Geschrieben wird in `admin_audit` (Betreiber), später in
 * `customer_audit` und `billing_audit`. Der Grund steht in
 * `docs/planning/portale-plan.md`: Eine gemeinsame Tabelle müsste in `core`
 * liegen, und `core` kennt keine Fachmodule — die ESLint-Zone würde das
 * erzwingen, und man umginge sie mit einer Abstraktion, die niemandem nützt.
 *
 * ## Warum der Akteur als Text mitgeschrieben wird
 *
 * `actorLabel` ist eine Kopie, kein Verbund. Ein Konto kann gelöscht oder
 * umbenannt werden; ein Prüfprotokoll, dessen Aussage sich rückwirkend ändert,
 * ist keines. Dieselbe Haltung wie bei `site_backup.repository`: Was
 * protokolliert wird, wird so protokolliert, wie es zum Zeitpunkt galt.
 */

export const AUDIT_ACTOR_TYPES = {
  /** Ein angemeldeter Betreiber. */
  adminUser: 'admin-user',
  /** Der statische Maschinenschlüssel `MASTER_ADMIN_API_KEY`. */
  apiKey: 'api-key',
  /** Die Plattform selbst — Zeitpläne, Reconciler, Wartungsläufe. */
  system: 'system',
} as const;

export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[keyof typeof AUDIT_ACTOR_TYPES];

export const auditActorTypeSchema = z.enum([
  AUDIT_ACTOR_TYPES.adminUser,
  AUDIT_ACTOR_TYPES.apiKey,
  AUDIT_ACTOR_TYPES.system,
]);

/** Ist der Vorgang durchgegangen? */
export const AUDIT_OUTCOMES = {
  succeeded: 'succeeded',
  failed: 'failed',
} as const;

export type AuditOutcome = (typeof AUDIT_OUTCOMES)[keyof typeof AUDIT_OUTCOMES];

export const auditOutcomeSchema = z.enum([AUDIT_OUTCOMES.succeeded, AUDIT_OUTCOMES.failed]);

/**
 * Der Vorgang, als stabiler Bezeichner in der Form `<bereich>.<verb>`.
 *
 * Bewusst eine Zeichenkette und keine Aufzählung: Jeder neue Endpunkt brächte
 * sonst eine Vertragsänderung mit sich, und ein vergessener Eintrag in der
 * Aufzählung ließe den Vorgang **unprotokolliert** durchgehen statt bloß
 * unbenannt. Die Form wird geprüft, die Werte nicht — die Prüfung, dass jeder
 * verändernde Vorgang protokolliert wird, leistet der Interceptor, nicht der
 * Typ.
 */
export const auditActionSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(/^[a-z][a-z\d-]*(\.[a-z][a-z\d-]*)+$/, {
    message: 'Muss die Form "<bereich>.<verb>" haben, z. B. "site.delete".',
  });

/** Ein Eintrag, wie ihn die Oberfläche anzeigt. */
export const auditEntryViewSchema = z.object({
  id: uuidSchema,
  occurredAt: isoDateTimeSchema,
  actorType: auditActorTypeSchema,
  /** `null` bei Maschinen- und Systemvorgängen — dort gibt es kein Konto. */
  actorId: uuidSchema.nullable(),
  /** Anzeigename zum Zeitpunkt des Vorgangs, z. B. die E-Mail-Adresse. */
  actorLabel: z.string().max(160),
  /** Die Rolle, unter der gehandelt wurde. */
  actorRole: z.string().max(32),
  ip: z.string().max(45).nullable(),
  correlationId: correlationIdSchema,
  action: auditActionSchema,
  /** Woran gehandelt wurde, z. B. `site`, `node`, `storage-box`. */
  targetType: z.string().max(32).nullable(),
  targetId: z.string().max(64).nullable(),
  /** Sprechende Bezeichnung des Gegenstands, z. B. der Hostname einer Site. */
  targetLabel: z.string().max(160).nullable(),
  outcome: auditOutcomeSchema,
  /**
   * Was sich geändert hat, sofern es der Use Case mitgeliefert hat.
   *
   * Der Interceptor füllt das **nicht** — er kennt keine Feldsemantik und
   * müsste raten. Ein geratener Vorher/Nachher-Vergleich wäre schlimmer als
   * keiner: Er sähe aus wie eine Auskunft.
   */
  changes: z
    .object({
      before: z.record(z.unknown()).nullable(),
      after: z.record(z.unknown()).nullable(),
    })
    .nullable(),
  /** Bei `failed` die Begründung auf Deutsch, sonst `null`. */
  message: z.string().max(500).nullable(),
});

export type AuditEntryView = z.infer<typeof auditEntryViewSchema>;

/** Eine Seite des Protokolls. */
export const auditPageSchema = z.object({
  entries: z.array(auditEntryViewSchema),
  /**
   * Der Zeiger auf die nächste Seite, oder `null` am Ende.
   *
   * Ein Zeiger und kein Seitenzähler: In ein Protokoll wird ständig vorne
   * geschrieben, und mit `OFFSET` verschöbe sich der Ausschnitt zwischen zwei
   * Abrufen — man sähe Einträge doppelt und andere nie.
   */
  nextCursor: z.string().nullable(),
});

export type AuditPage = z.infer<typeof auditPageSchema>;
